import { defineMessage, type MessageDescriptor } from 'react-intl'

export type VenueInspirationOption = 'featured' | 'beachfront' | 'cityEscapes' | 'garden_winery' | 'intimateWeddings'

interface WeddingVenueMessages {
  location: MessageDescriptor
  title: MessageDescriptor
  guestCapacity: MessageDescriptor
  season: MessageDescriptor
  seasonHint?: MessageDescriptor
  description: MessageDescriptor
  highlights: Array<MessageDescriptor>
}

export interface WeddingVenueListItem {
  offerId: string
  messages: WeddingVenueMessages
  optionType: VenueInspirationOption
}

export const WEDDING_VENUES_LIST: Array<WeddingVenueListItem> = [
  {
    offerId: '006RF00000OWJcoYAH',
    messages: {
      location: defineMessage({
        id: 'vPzDH5KKQEb33h5yIwFx',
        defaultMessage: 'Vietnam',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'oRZUmyWWwaHtWclcEZYd',
        defaultMessage: 'Victoria Hoi An Beach Resort & Spa',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'x__-hcQvJadWh7Ao9fYH',
        defaultMessage: '~109 Rooms',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: '5KBTsFbeSwajKxpXnsfb',
        defaultMessage: 'Feb - Jul',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'criNE7DUJuWTYwxCdXtb',
        defaultMessage: 'Dry season, before the wetter Central Vietnam winter',
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: '96ABqXBK27wykXyQDShl',
        defaultMessage: 'Exchange vows on a secluded stretch of Cua Dai Beach as golden light fades into evening. Blending French colonial charm with the rich heritage of Hoi An, this resort offers a romantic, laid-back setting where every detail - from lantern-lit gardens to oceanfront ceremonies - is curated by a dedicated wedding planning team.',
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'bKvmVz1X4jHbW-3zXFd9',
          defaultMessage: 'Private beachfront ceremonies on Cua Dai Beach as the sun sets over the South China Sea',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: '5cx5GyQEf4T4LXX1grth',
          defaultMessage: "Vietnamese, French and Japanese architectural influences throughout the resort's indoor spaces",
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: '8MGdO9PnRbVxJUoBqX19',
          defaultMessage: 'Lantern-lit garden pathways perfect for cocktail receptions and photography',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'featured',
  },
  {
    offerId: '006RF00000Yu52jYAB',
    messages: {
      location: defineMessage({
        id: 'ua6Eto7J7o1GtrxCY2sK',
        defaultMessage: 'Indonesia',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'iwD1Jvpx9jhXaTkIqEcr',
        defaultMessage: 'Ayodya Resort Bali',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'bM1Lf7HQQM83lZVYjMPf',
        defaultMessage: '~537 Rooms',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'XeZDsvPcsuSYg3OQr0rJ',
        defaultMessage: 'Apr - Oct',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'PQpCPjgmL2jY1J4VnkVe',
        defaultMessage: "Bali's dry season",
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'GWiBJ27RgRNvjX2kF2Zu',
        defaultMessage: 'Say "I do" on the beachfront of a genuine Balinese water palace, watched over by carved statues inspired by the Ramayana epic. From a torch-lit ceremony pavilion to a coconut grove reception under the stars, Ayodya blends grand architecture with warm Balinese tradition for a truly theatrical celebration.',
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'Bc6d3tSdvKQPK3aQ_vNH',
          defaultMessage: "Beachfront ceremony pavilion on one of Nusa Dua's most exclusive stretches of white sand",
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: '3nCgnD2lK3NLtPtvrBOP',
          defaultMessage: 'Traditional Balinese water-palace architecture with dramatic carved statues and cascading fountains',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'Yw3fv5E95h8XQMsDIfHX',
          defaultMessage: 'Reception in a fairy-light-lit private coconut grove, complete with Kecak dancers and fire performers',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'featured',
  },
  {
    offerId: '006RF00000OhlilYAB',
    messages: {
      location: defineMessage({
        id: 'ua6Eto7J7o1GtrxCY2sK',
        defaultMessage: 'Indonesia',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'owcfvqM3DOztTIhlGo8T',
        defaultMessage: 'The Apurva Kempinski Bali',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'ZSWvCFEYViiS7yH_lTeZ',
        defaultMessage: '~475 Rooms & Suites',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'XeZDsvPcsuSYg3OQr0rJ',
        defaultMessage: 'Apr - Oct',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'bkxiY5vl8sZH85PKLqvl',
        defaultMessage: 'Dry season',
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'AO0PCkWnKd-cWFb5vyOL',
        defaultMessage: 'Perched atop the majestic cliffs of Nusa Dua, The Apurva Kempinski Bali is an open-air theatre of Indonesian elegance. Say your vows in the light-filled Apurva Chapel before dancing the night away on the Ocean Front Lawn, where the Indian Ocean provides an unforgettable backdrop to every moment.',
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: '8PNyi9GlIy8jDJ9_ZB4F',
          defaultMessage: 'Striking bamboo-and-glass Apurva Chapel perched atop the Nusa Dua cliffs',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: '7W4YdtEq0a4fmzOGvNzT',
          defaultMessage: "Bali's largest garden beachfront wedding lawn, with panoramic Indian Ocean views",
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: '0Nv8aGkxJDBzELt9psOk',
          defaultMessage: 'Pillarless, naturally-lit Candi Ballroom for a show-stopping indoor reception',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'featured',
  },
  {
    offerId: '0062y000009BVOlAAO',
    messages: {
      location: defineMessage({
        id: 'Yl6Lf_XmmYgxqY2qzWxA',
        defaultMessage: 'Australia',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'MVjpf4Zbg0TIQMIMY75h',
        defaultMessage: 'InterContinental Hayman Island Resort',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'AkJ_sMtyGE47mhzG6THK',
        defaultMessage: '~182 Rooms, Suites & Villas',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'XeZDsvPcsuSYg3OQr0rJ',
        defaultMessage: 'Apr - Oct',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'LBPlJWLgNMKOeHmZ2VgJ',
        defaultMessage: "Whitsundays' dry season, cooler and less humid",
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'J_vjlVRdX-uYJcrOJrkd',
        defaultMessage: "Set within the heart of the Great Barrier Reef, InterContinental Hayman Island Resort is Australia's most iconic island wedding destination. Say your vows barefoot on pristine sand or from a hilltop chapel overlooking the Whitsundays, then celebrate under the stars with the reef as your playground.",
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'irN3uua0TrRTyAs6mBQO',
          defaultMessage: "Barefoot beachfront ceremonies on Hayman Island's private white-sand beach",
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'AN7ovUDiolaMKJx-7zVr',
          defaultMessage: 'Hilltop Stella Maris Chapel with sweeping views over the Whitsundays and the Coral Sea',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'SScDBPQb5CRpdfDE-XuQ',
          defaultMessage: 'Exclusive access to the Great Barrier Reef for unforgettable pre- or post-wedding experiences',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'featured',
  },
  {
    offerId: '006RF00000Xqyl4YAB',
    messages: {
      location: defineMessage({
        id: 'EvDYQ7nhr8Wnh2uupmzK',
        defaultMessage: 'Maldives',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'owPIKMIqJbUx8cIRltro',
        defaultMessage: 'Pullman Maldives Maamutaa',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'ffCuUbnPuXtEvDkWS71e',
        defaultMessage: '~122 Villas & Suites',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'o3wP-dg5jlkI6-M-o7I1',
        defaultMessage: 'Nov - Apr',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'Z2Q8PTa-KGLd4pmAlxho',
        defaultMessage: 'Maldives dry season',
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'Jn66Iz6xgqLSRxp6nyEI',
        defaultMessage: "Say \"I do\" on a private lagoon in the South Maldives, where overwater pavilions and secluded sandbanks set the stage for the ultimate intimate celebration. With underwater villas and personalised planning from Pullman's wedding team, every detail is designed for a truly private island escape.",
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'JeQ5imIG3LX7MseWxV-s',
          defaultMessage: 'Overwater wedding pavilion set against a private lagoon',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'aNmJCo7MX7q4-Zg-EgtN',
          defaultMessage: 'Rare underwater-bedroom Aqua Villas for an unforgettable honeymoon stay',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: '2F9cdmxXQcHzmNPceecx',
          defaultMessage: 'Private deserted-island ceremony options for total seclusion',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'featured',
  },
  {
    offerId: '0062y000008gGL1AAM',
    messages: {
      location: defineMessage({
        id: '6YjtlTak6Opq8wTb2IhO',
        defaultMessage: 'Coral Coast, Fiji',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'LcFNinJ2kb265eE4veF2',
        defaultMessage: 'Warwick Fiji',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: '67F9-TlHSXVNzzo_tCb3',
        defaultMessage: '~248 Rooms & Suites',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: '9V9Kgqh98dqC3NrU_uuc',
        defaultMessage: 'May - Oct',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'jyq898F6xkdL8g1RLhMR',
        defaultMessage: "Fiji's dry season",
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'ZmCiy7oJ82SP9S0WGC54',
        defaultMessage: 'Exchange vows on a private island fringed by the South Pacific, then dance the night away on the oceanfront Navau Deck as the Coral Coast glows in the evening light. With 28 acres of tropical gardens and dedicated onsite wedding planners, Warwick Fiji delivers barefoot island romance at every turn.',
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'M0sbiGqWmEKJr6d440fP',
          defaultMessage: "Private island ceremonies on Wicked Walu, Warwick's own island restaurant",
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'kYfWYwbhszMQvKoFNhjQ',
          defaultMessage: 'Purpose-built oceanfront Navau Deck for ceremonies and receptions under the stars',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'oXYylogwWbhuAuKy24j6',
          defaultMessage: '28 acres of lush tropical gardens for photography and outdoor celebrations',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'beachfront',
  },
  {
    offerId: '02b9e0d4-97df-4d7c-a0d4-334c51e7232d',
    messages: {
      location: defineMessage({
        id: 'YVjNuwWo-qDAa7vJeymR',
        defaultMessage: 'Thailand',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'FByqzcXFhiPQGVoxQqHp',
        defaultMessage: 'Phuket Marriott Resort & Spa, Merlin Beach',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'ODOxIf03wWIWzsHkyRrh',
        defaultMessage: '~414 Rooms',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'o3wP-dg5jlkI6-M-o7I1',
        defaultMessage: 'Nov - Apr',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'dID4pRnlZ2vvnm50PxTH',
        defaultMessage: "Thailand's dry season",
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: '2X8Atxhb7VIAFsuyKxt5',
        defaultMessage: "Set on Phuket's secluded Tri-Trang Beach, this resort offers a sophisticated escape from the island's busier shores. Say your vows on a sweeping beachfront lawn as the Andaman Sea shimmers behind you, before celebrating long into the night in one of Phuket's largest and most elegant ballrooms.",
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'fEG6_fM5nCQHHNeI81VW',
          defaultMessage: 'Secluded Tri-Trang Beach setting, away from the busier Patong strip',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'xgNGe7TfSxQI7hQrJWqh',
          defaultMessage: 'Expansive Beach Lawn accommodating both intimate and large-scale celebrations',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: '6mii0vU7hDbuvvqn9unH',
          defaultMessage: 'Contemporary Merlin Grand Ballroom as a stylish wet-weather backup',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'beachfront',
  },
  {
    offerId: '0062y000006zXGKAA2',
    messages: {
      location: defineMessage({
        id: '6YjtlTak6Opq8wTb2IhO',
        defaultMessage: 'Coral Coast, Fiji',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: '12VkzAkr9U1ZdhYLh8f8',
        defaultMessage: 'Shangri-La Yanuca Island, Fiji',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'mWYChGra6WjUdPgKet65',
        defaultMessage: '~443 Rooms, Suites & Bures',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: '9V9Kgqh98dqC3NrU_uuc',
        defaultMessage: 'May - Oct',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'jyq898F6xkdL8g1RLhMR',
        defaultMessage: "Fiji's dry season",
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'zZOddpJ3LCmHv5q1tpeJ',
        defaultMessage: 'Exchange vows in a glass-walled chapel overlooking the reef, then celebrate on a private island shaped by the rhythms of a traditional Fijian village. With turquoise lagoons on one side and the open Pacific on the other, Shangri-La Yanuca Island blends barefoot romance with polished five-star service.',
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'HTwvVcbiJWJVguLA-xsx',
          defaultMessage: 'Non-denominational Seaside Chapel with glass walls framing panoramic reef and ocean views',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: '8SOlgDmaw_0uv2gY92ny',
          defaultMessage: 'Set on a private 109-acre island connected to the mainland by a causeway',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'cv5axAPzcwU1GYykN67U',
          defaultMessage: 'Traditional Fijian village-inspired layout with lagoon and ocean-view accommodation',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'beachfront',
  },
  {
    offerId: '006RF00000OY7PRYA1',
    messages: {
      location: defineMessage({
        id: 'YVjNuwWo-qDAa7vJeymR',
        defaultMessage: 'Thailand',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'mVMDFJB0fkGvEXmY-Jyi',
        defaultMessage: 'Devasom Khao Lak Beach Resort & Villas',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'wjAkYXEPAu6bebYNjksL',
        defaultMessage: '~69 Rooms & Villas',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'o3wP-dg5jlkI6-M-o7I1',
        defaultMessage: 'Nov - Apr',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'dID4pRnlZ2vvnm50PxTH',
        defaultMessage: "Thailand's dry season",
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'HfbD3NAiry6wfHQd15Zb',
        defaultMessage: "Tucked between a natural lagoon and Khao Lak's finest sunset beach, Devasom is a boutique escape built for intimate celebrations. Say your vows barefoot on the sand as a vintage boat drifts on the horizon, then celebrate in lantern-lit gardens designed to feel like a private world of your own.",
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: '6eTqbHV4IaXc_XaEBBWL',
          defaultMessage: "Intimate beachfront ceremonies on one of Khao Lak's widest and most private stretches of sand",
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'JK78jy9XlqDrQ8zDXUcB',
          defaultMessage: 'Architecture inspired by a 7th-century lost maritime civilisation, giving every corner a storybook feel',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'zJ4EOg2jf4PVjXmt1lWP',
          defaultMessage: 'A vintage Thai boat anchored offshore, repositioned daily as a natural photo backdrop',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'beachfront',
  },
  {
    offerId: '006RF00000Utey1YAB',
    messages: {
      location: defineMessage({
        id: 'ua6Eto7J7o1GtrxCY2sK',
        defaultMessage: 'Indonesia',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'ifE0EM1X3v1OBSkgwLmc',
        defaultMessage: 'Merusaka Nusa Dua Bali',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'KNgiOpW5Q69ZER396nP8',
        defaultMessage: '~455 Rooms, Suites & Villas',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'XeZDsvPcsuSYg3OQr0rJ',
        defaultMessage: 'Apr - Oct',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'bkxiY5vl8sZH85PKLqvl',
        defaultMessage: 'Dry season',
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'i_-jmwxc22iRxN--QLs7',
        defaultMessage: 'Say "I do" in a glass-walled chapel where the sky and Indian Ocean seem to merge into one. Inspired by the Seven Balinese Goddesses, Merusaka Nusa Dua pairs beachfront ceremony spaces with lush gardens, offering a setting as spiritually rich as it is romantic.',
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'D2e-yjbjw5GsSwqO8Db8',
          defaultMessage: 'Glass-walled Wedding Chapel with uninterrupted Indian Ocean views',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'SM1LwXZtncEaNWmqWvSI',
          defaultMessage: "Direct access to Mengiat Beach, one of Nusa Dua's most beautiful stretches of white sand",
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'MTQFwQjSmMORMMujEMGU',
          defaultMessage: "Architecture inspired by the Seven Balinese Goddesses, woven throughout the resort's design",
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'beachfront',
  },
  {
    offerId: '006RF00000VKOfSYAX',
    messages: {
      location: defineMessage({
        id: 'YVjNuwWo-qDAa7vJeymR',
        defaultMessage: 'Thailand',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'UDwuvbWmcl8ca4UzAPof',
        defaultMessage: 'Katathani Phuket Beach Resort',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'cAbYnQpgmgph1Kf_5iPt',
        defaultMessage: '~518 Rooms & Suites',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'o3wP-dg5jlkI6-M-o7I1',
        defaultMessage: 'Nov - Apr',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'dID4pRnlZ2vvnm50PxTH',
        defaultMessage: "Thailand's dry season",
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'P1_zyJA5JgZ6Np4SY0DI',
        defaultMessage: 'Set along an 850-metre stretch of pristine Kata Noi Beach, Katathani has long been a pioneer of destination weddings in Phuket. Walk the aisle on a picture-perfect beachfront lawn before moving into an open-air marquee reception, guided by a wedding team with years of experience turning big days into unforgettable ones.',
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'L8F0AZ9s9uOlk_-WYj6u',
          defaultMessage: "Beachfront lawn along an 850-metre stretch of Kata Noi Beach, one of Phuket's most scenic",
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'Q0gsrvFUy_pygUIJtnqz',
          defaultMessage: "One of Phuket's most experienced wedding teams, having coordinated hundreds of ceremonies",
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: '3NFJ3WRn2YERISQz53a7',
          defaultMessage: 'Dedicated Wedding Gallery for bridal preparations, just steps from the ceremony lawn',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'beachfront',
  },
  {
    offerId: '0062y000004UNTDAA4',
    messages: {
      location: defineMessage({
        id: 'YVjNuwWo-qDAa7vJeymR',
        defaultMessage: 'Thailand',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: '47HkrgynoPFr4IZcHezl',
        defaultMessage: 'Banyan Tree Bangkok',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'p8V4LBaRU-HIsyxO29GU',
        defaultMessage: '~327 Rooms & Suites',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: '7q5z1Xdj5lAA1iIXtKrT',
        defaultMessage: 'Nov - Feb',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'bg_e1G1R43Z6D9DGRjNY',
        defaultMessage: "Bangkok's cooler, drier months",
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'tvOz_G_3vDwcSp_FZ4uU',
        defaultMessage: "Say your vows high above the city lights at Vertigo, one of Bangkok's most iconic rooftop settings, or drift down the Chao Phraya River aboard a private cruise. Banyan Tree Bangkok turns a cosmopolitan skyline into the ultimate romantic backdrop for couples who want their wedding to feel larger than life.",
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'X6jIo5sYtRH5W09UYA0X',
          defaultMessage: 'Rooftop ceremonies at Vertigo, 61 floors above the city with 360-degree skyline views',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'EaLfUArAUE8ap-4XGQ7o',
          defaultMessage: 'Option to celebrate aboard the Saffron Cruise along the historic Chao Phraya River',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'QPnJJjPqwA06jlyZWq75',
          defaultMessage: "One of Bangkok's tallest hotels, offering a genuinely dramatic urban wedding backdrop",
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'cityEscapes',
  },
  {
    offerId: 'fdda3ba5-5e65-4650-8504-285ffa715ae6',
    messages: {
      location: defineMessage({
        id: 'T2nyIs00D_Da93Y4FrZU',
        defaultMessage: 'Japan',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'pAMsjhgxjJdffcfCjS7N',
        defaultMessage: 'Hyatt Regency Tokyo',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'Tjd4_7ipg-8ka1QTTSUX',
        defaultMessage: '~746 Rooms & Suites',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'wAVELmJdb_9EjXm5YKjF',
        defaultMessage: 'Mar - May & Sep - Nov',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'EsflybYbcbIFgY4qkjlp',
        defaultMessage: 'Cherry blossom and autumn foliage seasons',
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'UARoRp0cTA64y7wSoMc7',
        defaultMessage: 'In the heart of Shinjuku, Hyatt Regency Tokyo offers the rare choice between a refined Western chapel and a traditional Shinto ceremony room - all beneath sweeping views of the Tokyo skyline. With an onsite bridal boutique and dedicated wedding coordinators, every detail is designed to feel effortlessly elegant.',
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'i31m0k6IW1lIIeKCW9H0',
          defaultMessage: 'Refined onsite chapel alongside a traditional Shinto Ceremony Room, for Western or Japanese-style weddings',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'Rf6GsPdsVBrspTo93vy6',
          defaultMessage: 'Two penthouse-level sky banquet rooms with panoramic Shinjuku skyline views',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'tK49veQtwFDTj-EWoIjH',
          defaultMessage: 'Onsite Bridal Boutique with over 100 gowns from leading designers',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'cityEscapes',
  },
  {
    offerId: '0062y000003IAJ9AAO',
    messages: {
      location: defineMessage({
        id: 'YVjNuwWo-qDAa7vJeymR',
        defaultMessage: 'Thailand',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'KoOnqUtNodXGTmxHKerm',
        defaultMessage: 'Siam Kempinski Hotel Bangkok',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'JyEwlVOzl0mYEvoZ7psB',
        defaultMessage: '~397 Rooms & Suites',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: '7q5z1Xdj5lAA1iIXtKrT',
        defaultMessage: 'Nov - Feb',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'bg_e1G1R43Z6D9DGRjNY',
        defaultMessage: "Bangkok's cooler, drier months",
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: '5SZw1D3szvQ6vbh1Tr9O',
        defaultMessage: 'Set on the grounds of a former royal palace garden, Siam Kempinski Bangkok blends European grandeur with Thai artistry. Exchange vows on the fountain-lined Rotunda Lawn before moving into the pillarless Chadra Ballroom, where lotus motifs and lush greenery carry the romance of the gardens indoors.',
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'OnqfYvFJty8Kj3jCm4qW',
          defaultMessage: 'Rotunda Lawn ceremony space surrounded by pools, fountains and lush palm gardens',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'OH5E956mTsjXQ4ZAf9oB',
          defaultMessage: 'Built on the site of the former royal gardens of Sra Pathum Palace, with genuine royal wedding history',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: '2HuftIzkfhHEQ_Y6vAux',
          defaultMessage: 'Pillarless 900 sqm Chadra Ballroom for a grand, contemporary reception',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'cityEscapes',
  },
  {
    offerId: '9c3c14ab-1cd7-4d46-9bcd-45134c87355a',
    messages: {
      location: defineMessage({
        id: 'Yl6Lf_XmmYgxqY2qzWxA',
        defaultMessage: 'Australia',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'oLBIFJEignG3yoRSAsks',
        defaultMessage: 'Crown Towers Sydney',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'jca9EHeYz0S2zVB609AD',
        defaultMessage: '~349 Rooms, Suites & Villas',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'CNUbpHB3YtBlXv43DUU4',
        defaultMessage: 'Sep - Nov & Mar - May',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'LQ4XSx1zxymoPOvSowOl',
        defaultMessage: "Sydney's shoulder seasons",
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'cOplpOS2IgUxztDaWh_c',
        defaultMessage: 'Say your vows with the Sydney Opera House and Harbour Bridge as your backdrop at Crown Towers Sydney. From the light-filled Pavilion to the glittering Pearl Ballroom, this Barangaroo landmark delivers a wedding as bold and unforgettable as the skyline itself.',
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'kltW-zRhNhqLHeMk2Z_8',
          defaultMessage: 'Sweeping Sydney Harbour views, including the Opera House and Harbour Bridge, from the Pavilion ceremony space',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'jV9nXRzkLssX_E5UTLp8',
          defaultMessage: 'Grand Pearl Ballroom with soaring ceilings and statement chandeliers for large-scale receptions',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'Lnc1o25pwphYVShVl5Nq',
          defaultMessage: 'Sculptural, architecturally iconic building in the heart of Barangaroo',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'cityEscapes',
  },
  {
    offerId: '006RF00000WfEEIYA3',
    messages: {
      location: defineMessage({
        id: '4u9u5m8toEhfcfdfvJZ8',
        defaultMessage: 'Singapore',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'MnR3WiDXDcUh49KYZ0uF',
        defaultMessage: 'Amara Singapore',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'ml8K7vLjfh27JdQBLSwM',
        defaultMessage: '~389 Rooms & Suites',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: '_Vl-SM95NtDCX8XsWcMj',
        defaultMessage: 'Feb - Apr',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'MxrI2YgpOq44csHr7NQv',
        defaultMessage: "Singapore's drier months",
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'y-Gwg9lKGt8g32jiyegg',
        defaultMessage: "Walk a striking 38-metre aisle beneath a star-flecked ceiling in Amara Singapore's column-free Grand Ballroom. Bathed in natural light and set in the heart of the city's Central Business District, it's a polished, contemporary stage for couples who want big-city glamour without leaving downtown Singapore.",
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'qQDOJHaM5ff_ZgAbSWm_',
          defaultMessage: 'Column-free Grand Ballroom with a dramatic 38-metre march-in aisle',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: '7dWMJqdw2I-a_q9K5EsY',
          defaultMessage: 'Floor-to-ceiling windows flooding the ballroom with natural light',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'xOu-C3bSZClHDxgmTF7S',
          defaultMessage: "Central Tanjong Pagar location, steps from Singapore's heritage districts",
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'cityEscapes',
  },
  {
    offerId: 'c436c5c4-aa12-486b-84c4-8f08719c171e',
    messages: {
      location: defineMessage({
        id: '4u9u5m8toEhfcfdfvJZ8',
        defaultMessage: 'Singapore',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'BAuTwWtPNcOE4ae23mGA',
        defaultMessage: 'The Ritz-Carlton, Millenia Singapore',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'VcGWf7DMlrsQFISQL_Fc',
        defaultMessage: '~608 Rooms & Suites',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: '_Vl-SM95NtDCX8XsWcMj',
        defaultMessage: 'Feb - Apr',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'MxrI2YgpOq44csHr7NQv',
        defaultMessage: "Singapore's drier months",
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: '6Li8Widl8gzVkLKkFzuN',
        defaultMessage: "Walk a chandelier-lit, 35-metre aisle in one of Marina Bay's most celebrated ballrooms. With panoramic skyline views and a museum-worthy art collection throughout, The Ritz-Carlton, Millenia Singapore pairs old-world grandeur with the energy of one of Asia's most exciting cities.",
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: '7f_goyuLSP-y62danYJE',
          defaultMessage: '35-metre flower-lined aisle beneath glittering chandeliers in the Grand Ballroom',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'jKrNLsPaRQVcHDgVKqPK',
          defaultMessage: 'Iconic octagonal bathroom windows framing Marina Bay skyline views',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'fbvqek5REU3g2s65Z8vm',
          defaultMessage: 'Home to a 4,200-piece contemporary art collection worth an estimated SGD 5 million',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'cityEscapes',
  },
  {
    offerId: '0062y000009ErHOAA0',
    messages: {
      location: defineMessage({
        id: 'DHvUWCSq6ULhECJ3UBec',
        defaultMessage: 'Mornington Peninsula, Australia',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'y2fnl4beRdIQqbeykf-Z',
        defaultMessage: 'Lancemore Lindenderry Red Hill',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'BuGaFLS2NBZaOrOJuABS',
        defaultMessage: '~40 Rooms',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'OLNpYVk2eixA_msVHpJs',
        defaultMessage: 'Oct - Apr',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'pewVlTr7ATx4t8txl2oD',
        defaultMessage: "Mornington Peninsula's warmer months",
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'NykKbbh0LLWBbMipRmKm',
        defaultMessage: 'Say "I do" beside a mirror-still lake framed by silver birch trees, then walk a cypress-lined vineyard aisle at this boutique Mornington Peninsula estate. With ceremony, reception and accommodation all in one place, Lancemore Lindenderry offers a relaxed, elegant countryside wedding just an hour from Melbourne.',
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'A-hfjQw7ygoyl4VoMrVi',
          defaultMessage: 'Ceremony space overlooking a tranquil lake framed by silver birch trees',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'OQYLNImeguaXBsAaL-fP',
          defaultMessage: 'The Vineyard aisle, flanked by manicured cypress trees and rows of vines',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'J9noMekarRQYujCXgo-8',
          defaultMessage: 'Light-filled Lakeview reception space set within 34 acres of gardens and vineyards',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'garden_winery',
  },
  {
    offerId: '006RF00000JILW7YAP',
    messages: {
      location: defineMessage({
        id: 'DHvUWCSq6ULhECJ3UBec',
        defaultMessage: 'Mornington Peninsula, Australia',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: '9Zqx0OMYydf06MxkIcLW',
        defaultMessage: 'Jackalope Hotel',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'ovYHBEgRT2y_aZ5JR9lX',
        defaultMessage: '~45 Rooms',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'OLNpYVk2eixA_msVHpJs',
        defaultMessage: 'Oct - Apr',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'pewVlTr7ATx4t8txl2oD',
        defaultMessage: "Mornington Peninsula's warmer months",
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'xMlrtluxtgVOrwMyinq5',
        defaultMessage: 'Take over an entire architectural icon perched above Willow Creek Vineyard. With a striking black infinity pool, gallery-worthy interiors and vineyard views at every turn, Jackalope offers couples a wedding weekend that feels as daring and design-led as the hotel itself.',
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'gzzNcaQmA0T8Fve2vdF-',
          defaultMessage: 'Full exclusive-use hotel buyouts, transforming the whole property into a private wedding weekend',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'vtn8ZZC0DBh8YCVdc8yk',
          defaultMessage: 'Ceremonies on the terrace overlooking the Willow Creek Vineyard, with a 30-metre black infinity pool nearby',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'NXWsHQIQ22A0jKGck4ms',
          defaultMessage: "Bold, art-filled interiors designed with some of Australia's leading creatives",
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'garden_winery',
  },
  {
    offerId: 'aa4ac49b-d0f9-4164-b306-9662ad8f96ac',
    messages: {
      location: defineMessage({
        id: 'uvnTdLZqIvE8xg_YLYb6',
        defaultMessage: 'Yarra Valley, Australia',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'kJ_OYvcGLr8iLPWC6pSw',
        defaultMessage: 'Balgownie Estate',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'qBRZJvGHOF11UB05Bl7v',
        defaultMessage: '~70 Rooms',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'S69EK58qaN1Pf63ylfeq',
        defaultMessage: 'Sep - Apr',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'uGj_2r5q7AjMVIPMOxdD',
        defaultMessage: "Yarra Valley's warmer, drier months",
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'lTDyKsT4XEd_ND-dvrk4',
        defaultMessage: "Exchange vows among rolling vineyards just an hour from Melbourne, then raise a glass of the estate's own five-star-rated wine as the sun sets over the Yarra Ranges. With everything from ceremony to spa and accommodation on one property, Balgownie Estate makes for an effortless, all-in-one wine country wedding.",
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'gt8YCi6ZYLALwZo8L_sk',
          defaultMessage: 'Ceremony and reception both possible on-site across 75 acres of vineyards',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: '_fZRo86BH3uFBn11Ikxc',
          defaultMessage: "Five-Star Halliday-rated estate wines served at the couple's own reception",
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'nNWrFn9ru2YtTJVi4bl5',
          defaultMessage: 'Onsite spa, restaurant, and accommodation for a true weekend-away wedding',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'garden_winery',
  },
  {
    offerId: 'b309d5a8-2d1e-44ab-895c-524bd38a930f',
    messages: {
      location: defineMessage({
        id: 'wb7JTpmh9s43So9eE4oz',
        defaultMessage: 'Mekong Delta, Vietnam',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: '3SwRq70obqBhMrsvMSIs',
        defaultMessage: 'Victoria Can Tho Resort',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'X39Q6XkET4F2kATh6e44',
        defaultMessage: '~92 Rooms & Suites',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: '-eKSdis7xI2koOHO67MW',
        defaultMessage: 'Dec - Apr',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'Asp1pvQx_vqjKBDD0zoO',
        defaultMessage: "Mekong Delta's dry season",
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'EiOS4kHcSIaYvYmUAqIg',
        defaultMessage: 'Set on the banks of the Hau River in the heart of the Mekong Delta, Victoria Can Tho blends colonial elegance with authentic riverside charm. Say your vows in a lush riverside garden before celebrating with a sunset cruise on a traditional rice barge - an intimate wedding infused with the rhythm of the Mekong.',
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'z6l3xuoD5pmgUHegYrzv',
          defaultMessage: 'Riverside garden ceremonies overlooking the Hau River',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'oADVSbqhxkPi-YWgXBst',
          defaultMessage: 'Elegant Indochine architecture blending French colonial and Vietnamese heritage',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'vFPZCqu5k9vQDJwjYOc_',
          defaultMessage: 'Sunset cruises on a traditional wooden rice barge for a truly local touch',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'intimateWeddings',
  },
  {
    offerId: '006RF00000TWd2MYAT',
    messages: {
      location: defineMessage({
        id: 'TjzOj8C0wbJtouxGTmo7',
        defaultMessage: 'Hunter Valley, Australia',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'tmanSPKsA9fj5dsb5U8Y',
        defaultMessage: 'Chateau Elan at The Vintage',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'HV3hAivtrCwTEdWjeym3',
        defaultMessage: '~100 Rooms & Suites',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'wAVELmJdb_9EjXm5YKjF',
        defaultMessage: 'Mar - May & Sep - Nov',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'YGU4mzUdTLj26ta6YsFF',
        defaultMessage: "Hunter Valley's mild seasons",
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'opR9t6wrfy41EGn3ZhBL',
        defaultMessage: "Exchange vows in a handcrafted sandstone chapel shaded by gum trees, with the Hunter Valley's vineyards rolling out beyond. Chateau Elan pairs old-world charm with modern indulgence - an award-winning spa, a championship golf course, and elegant event spaces make it a wedding destination guests won't want to leave.",
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'rQDM1YIbnhIEbrDQ58aB',
          defaultMessage: 'Handcrafted, convict-cut sandstone Carriage House chapel shaded by gum trees',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'z07tA7m3mNCH8Cg2LFnX',
          defaultMessage: 'Ceremony sites overlooking a Greg Norman-designed golf course and rolling vineyards',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'eZAx21xcos6w3V4OEXcP',
          defaultMessage: 'Onsite Hunter Valley Day Spa with 17 treatment rooms for pre-wedding pampering',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'intimateWeddings',
  },
  {
    offerId: '006RF00000Wh2owYAB',
    messages: {
      location: defineMessage({
        id: 'AGgUNiQ5Bdl3iVJpFbbO',
        defaultMessage: 'Bali, Indonesia',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'IV6sNnQ12PL7tdpxMU0R',
        defaultMessage: 'Kappa Senses Ubud',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'gFv5ed8n2vriL5gR9_qE',
        defaultMessage: '~76 Rooms & Villas',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'XeZDsvPcsuSYg3OQr0rJ',
        defaultMessage: 'Apr - Oct',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'PQpCPjgmL2jY1J4VnkVe',
        defaultMessage: "Bali's dry season",
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'Hobao1SdsGMk1lO6XKhb',
        defaultMessage: 'Say your vows surrounded by emerald rice paddies at this eco-conscious French-designed retreat in Ubud. Inspired by the Balinese legend of Rama and Shinta, Kappa Senses blends romance, sustainability and quiet luxury for couples seeking an intimate celebration close to nature.',
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'QawOppBQVkZHyHx63JzI',
          defaultMessage: 'Ceremonies set amid vibrant rice paddies inspired by the tale of Rama and Shinta',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: '0QIfdwskisYAKxfIapwy',
          defaultMessage: 'Eco-conscious, French-designed resort with a working permaculture garden',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'O-3CqItkC9sWlaMfEQj-',
          defaultMessage: 'OmTara Spa by Clarins for an indulgent pre-wedding retreat',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'intimateWeddings',
  },
  {
    offerId: '006RF00000VPN1BYAX',
    messages: {
      location: defineMessage({
        id: '8x3l-cbi1eSOgoU5zGMl',
        defaultMessage: 'Ubud, Bali',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: '-9AVH7Cacz3OsrpTSl0V',
        defaultMessage: 'Tanah Gajah, a Resort by Hadiprana',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'ScGpI57lkF4_crYD4wkL',
        defaultMessage: '~24 Suites & Villas',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'XeZDsvPcsuSYg3OQr0rJ',
        defaultMessage: 'Apr - Oct',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'PQpCPjgmL2jY1J4VnkVe',
        defaultMessage: "Bali's dry season",
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'MiBnr_T2yCdy2svY3rr0',
        defaultMessage: 'Say your vows beneath the historic Elephant Gate, once part of the private Hadiprana family estate. Set across six hectares of rice paddies and gardens, Tanah Gajah offers couples an intimate, art-filled sanctuary - with the option to buy out the entire resort for family and friends.',
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'zlY4QyF2ctjBDlletnNq',
          defaultMessage: 'Ceremonies beneath the historic Elephant Gate, a remnant of the original Hadiprana family estate',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'TlskxR8wBNU3P0JGfq3a',
          defaultMessage: 'Full private resort buyouts across a six-hectare estate of rice paddies and gardens',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: '0BWXIR6qwgwcOxi-gTy2',
          defaultMessage: 'Curated collection of original Balinese art throughout the property',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'intimateWeddings',
  },
  {
    offerId: '006RF00000NuworYAB',
    messages: {
      location: defineMessage({
        id: 'EvDYQ7nhr8Wnh2uupmzK',
        defaultMessage: 'Maldives',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'h8N3ielW4PiX2PR0VCMA',
        defaultMessage: 'Park Hyatt Maldives Hadahaa',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'ly4aVlqYy3gbilBuQjdG',
        defaultMessage: '~51 Villas',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: '-eKSdis7xI2koOHO67MW',
        defaultMessage: 'Dec - Apr',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: '1_KgTGwk89PRAkjmpOSX',
        defaultMessage: "Maldives' dry season",
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'RVouADDlf5VrNy32KITO',
        defaultMessage: "Exchange vows on a remote private island encircled by one of the Maldives' most pristine coral reefs. With just 51 minimalist villas set between beach and lagoon, Park Hyatt Maldives Hadahaa offers barefoot, unhurried romance far from the crowds.",
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'wSerKNfyDTsv-NJ5yaS9',
          defaultMessage: "One of the Maldives' last remaining pristine 360-degree house reefs, just steps from shore",
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'hn-8kPfaKwptJLSiNUx4',
          defaultMessage: 'Overwater and beach villas designed with minimalist Maldivian timber and stone',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'nrr8F6WbmzMNRQqJrSIe',
          defaultMessage: 'Genuine seclusion - one of the most remote and private islands in the Maldives',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'intimateWeddings',
  },
]
